import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';
import Button from './ui/Button';

/**
 * The visible face of a failed session boot.
 *
 * WHY THIS EXISTS: `Account/GetUserInfo` is the one call the whole shell depends
 * on (role, userId, company). It is slow cold (~31s measured) and, before the
 * accompanying fixes, a hang or a reject left the app in a state with NO output
 * at all — the user slice stayed at its reducer initial value (role "", userId
 * null), the nav collapsed to the empty-role set, the subscription hook never
 * fired because it gates on userId, and MasterLayout rendered a skeleton
 * forever WITHOUT EVER SENDING AN HTTP REQUEST. No 4xx, no 5xx, no console
 * error, no retry — an invisible permanent hang that cost a full investigation
 * to explain.
 *
 * So the rule now is: a boot that cannot complete must SAY SO and must offer a
 * way out. Never a silent skeleton.
 */

export interface BootErrorPanelProps {
  /** Re-runs the boot fetch. Must be safe to call repeatedly. */
  onRetry: () => void;
  /** Set while a retry is in flight so the button can't be double-fired. */
  isRetrying?: boolean;
}

const BootErrorPanel: React.FC<BootErrorPanelProps> = ({ onRetry, isRetrying = false }) => {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="boot-error-panel"
      className="flex min-h-screen w-full items-center justify-center bg-background p-6"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="mt-0.5 shrink-0 text-destructive" />
          <div className="min-w-0 space-y-2">
            <p className="text-base font-semibold text-foreground">
              {t("Couldn't load your profile")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("We couldn't finish loading your account. Check your connection, then try again.")}
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            variant="default"
            size="sm"
            onClick={onRetry}
            loading={isRetrying}
            disabled={isRetrying}
            iconName="RefreshCw"
          >
            {t('Retry')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BootErrorPanel;
